import { Box, BoxProps } from '@mui/material';
import { useMemo } from 'react';

import { convert } from '@/common/convert';
import { DirectionIconProps } from '@/components/data/DirectionIcon';

export type SpreadIconProps = DirectionIconProps & {
    spread: number;
};

export const SpreadIcon = ({ heading, type, unit = 'deg', spread, sx, ...props }: SpreadIconProps & BoxProps) => {
    const rotation = useMemo(() => {
        const headingDegrees = convert(heading).from(unit).to('deg');
        switch (type) {
            case 'source':
                return Math.round((headingDegrees + 180) % 360);
            case 'flow':
                return Math.round(headingDegrees % 360);
        }
    }, [heading, type, unit]);

    // length of overall radius (arbitrary, since we'll scale the viewBox based on this)
    const r = 10;
    // fraction of radius that the sector fills
    const fillFraction = 0.8;
    // fraction of radius that the lines fill (leave a little space for the arrowhead)
    const lineFraction = 0.75;
    const halfSpreadRadians = Math.min(convert(spread).from(unit).to('rad') / 2, Math.PI - 0.0001);
    // spread is more than half a circle; need to adjust layout
    const overHalf = halfSpreadRadians > Math.PI / 2;
    const angleX = Math.sin(halfSpreadRadians) * r;
    const angleY = Math.cos(halfSpreadRadians) * r;

    const viewBox = overHalf ? [-r, -r, r * 2, r * 2] : [-angleX, -r, angleX * 2, r];

    /* 
    Always draw a V with the vertex at 0,0, open to the top (negative Y), and use CSS rotation to orient it toward (or away from)
    the heading. For <180 spreads, the viewbox is as tall as the radius and as wide as necessary to fit the arms, and SVG proportional
    scaling will make it as big as it can within the 1em square. For wider spreads, we just make the viewbox 2r square around the vertex.
     */
    return (
        <Box
            component="svg"
            width="1em"
            height="1em"
            viewBox={viewBox.join(' ')}
            sx={{ transform: `rotate(${rotation}deg)`, fontSize: '1.25rem', width: '1.5em', ...sx }}
            {...props}
        >
            <defs>
                <marker
                    id="arrow"
                    viewBox="0 0 10 10"
                    refX="5"
                    refY="5"
                    markerWidth="4"
                    markerHeight="4"
                    orient="auto-start-reverse"
                    fill="currentColor"
                >
                    <path d="M 5 0 L 10 5 L 5 10 z" />
                </marker>
            </defs>

            <line
                x1="0"
                y1="0"
                x2={-angleX * lineFraction}
                y2={-angleY * lineFraction}
                stroke="currentColor"
                strokeWidth={1}
                strokeLinecap="round"
                markerEnd="url(#arrow)"
            />
            <line
                x1="0"
                y1="0"
                x2={angleX * lineFraction}
                y2={-angleY * lineFraction}
                stroke="currentColor"
                strokeWidth={1}
                strokeLinecap="round"
                markerEnd="url(#arrow)"
            />
            <path
                d={`
                    M ${-angleX * fillFraction} ${-angleY * fillFraction}
                    A 9 9 0 ${overHalf ? 1 : 0} 1 ${angleX * fillFraction} ${-angleY * fillFraction}
                    L 0 0
                    Z
                `}
                stroke="none"
                fill="currentColor"
                fillOpacity={0.5}
            />
        </Box>
    );
};
